import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  FlxButtonModule,
  FlxErrorComponent,
  FlxFileUploadEvent,
  FlxFileUploadModule,
  FlxIconModule,
} from '@flowx/angular-ui-toolkit'
import {
  FileUploadTemplateConfig,
  LabelAccessibilityProps,
  ProcessActionTriggerEvent,
  ResolvedAction,
  emitRejectedUploadAnalytics,
  getActionsByTemplateConfigAndTrigger$,
  getCurrentReadOnlyState,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal, getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-upload-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxButtonModule,
    FlxFileUploadModule,
    FlxLocalizePipe,
    FlxErrorComponent,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './upload-wrapper.component.html',
  styleUrl: './upload-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxUploadWrapperComponent implements OnInit, AfterViewInit {
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #destroyRef = inject(DestroyRef)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<FileUploadTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)
  variant = computed(() => this.flowxProps().type || 'ghost')

  icons = computed(() => {
    const { icon } = this.flowxProps()
    return { icon: getContentIcon(icon) }
  })

  errors = signal([])

  // expressions
  isHidden: Signal<boolean>
  isDisabled: Signal<boolean>
  // dynamic props
  label: Signal<string>
  ariaLabel: Signal<string>
  readonly = getCurrentReadOnlyState

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const { label, hide, disabled } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.isDisabled = getBoolExpressionSignal(disabled, this.templateConfig(), {
        logType: 'disabled',
      })
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.ariaLabel = computed(() => this.accessibility()?.label)
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  async onSelectFile({ file, errors }: FlxFileUploadEvent): Promise<void> {
    runInInjectionContext(this.#injector, () => {
      this.errors.set(
        Object.keys(errors).map((error) => ({
          message: getDynamicPropSignal(
            this.flowxProps()[error] || errors[error],
            this.templateConfig()
          ),
        }))
      )
    })
    const errorKeys = Object.keys(errors)
    if (errorKeys.length > 0 && file) {
      // Rejected by the toolkit: no request starts, so core never sees this attempt.
      emitRejectedUploadAnalytics(
        this.#clickActions().map(({ action }) => action),
        this.templateConfig(),
        [file],
        errorKeys
      )
    }
    if (this.errors().length === 0 && file) {
      const formData = new FormData()
      formData.append('file', file)
      await this.#actionService.runActions(this.#clickActions(), this.templateConfig(), formData)
    }
  }
}
